import { InputNumberLocalized } from '@components'

import { IconButton, Input } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { AccountSelectWithCreate } from '@features/chartOfAccounts/elements/AccountSelectWithCreate'
import type { Account } from '@modules-deprecated/app/accounts/types'
import type { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { useFormContext } from '../../../../../../../../../../hooks'
import type { ReviewEntryDataFormSchema } from '../../../../../../../../types/getReviewEntryDataFormSchema'
import { ReviewEntryVatRateSelect } from '../../../../../../../ReviewEntryVatRateSelect'
import * as Styled from '../../styles'
import { ReviewEntryLineItemReadOnlyAmountField } from './elements/ReviewEntryLineItemReadOnlyAmountField'

interface ReviewEntryLineItemRowProps {
  isFormReady: boolean
  isReadOnly: boolean
  lineIndex: number
  onAccountChange: (lineIndex: number, accountId: string | undefined, account: Account | undefined) => void
  onAmountExclVatChange: (lineIndex: number, amountExclVat: number | undefined) => void
  onAmountInclVatChange: (lineIndex: number, amountInclVat: number | undefined) => void
  onDeleteLine: (lineIndex: number) => void
  onVatRateChange: (lineIndex: number, vatRateId: string | undefined, taxRate: TaxRate | undefined) => void
  showDeleteAction: boolean
}

export const ReviewEntryLineItemRow = ({
  isFormReady,
  isReadOnly,
  lineIndex,
  onAccountChange,
  onAmountExclVatChange,
  onAmountInclVatChange,
  onDeleteLine,
  onVatRateChange,
  showDeleteAction,
}: ReviewEntryLineItemRowProps): ReactElement => {
  const { t } = useTranslation()
  const { FormItem } = useFormContext<ReviewEntryDataFormSchema>()
  const isDisabled = !isFormReady || isReadOnly

  const handleDeleteClick = useCallback(() => {
    onDeleteLine(lineIndex)
  }, [lineIndex, onDeleteLine])

  return (
    <Styled.ReviewEntryLineItemRow role="row">
      <Styled.ReviewEntryLineItemCell role="cell">
        <Styled.ReviewEntryLineItemFormItemWrapper>
          <FormItem
            name={`entryLines.${lineIndex}.accountId`}
            render={({ field: { error, id, value } }) => (
              <>
                <Styled.ReviewEntryLineItemFieldLabel htmlFor={id}>
                  {t('review_entries.detail.line_items.header.account_number')}
                </Styled.ReviewEntryLineItemFieldLabel>
                <Styled.ReviewEntryLineItemCellField>
                  <AccountSelectWithCreate
                    disabled={isDisabled}
                    error={error}
                    id={id}
                    onSelect={(selectedId, account) => onAccountChange(lineIndex, selectedId, account)}
                    placeholder={t('review_entries.detail.line_items.field.expense_category.placeholder')}
                    selectedId={value ?? undefined}
                    size="sm"
                  />
                </Styled.ReviewEntryLineItemCellField>
              </>
            )}
          />
        </Styled.ReviewEntryLineItemFormItemWrapper>
      </Styled.ReviewEntryLineItemCell>
      <Styled.ReviewEntryLineItemCell role="cell">
        <Styled.ReviewEntryLineItemFormItemWrapper>
          <FormItem
            name={`entryLines.${lineIndex}.description`}
            render={({ field: { error, ...field } }) => (
              <Styled.ReviewEntryLineItemCellField>
                <Input
                  {...field}
                  aria-label={t('review_entries.detail.line_items.header.label')}
                  disabled={isDisabled}
                  error={error}
                  placeholder={t('review_entries.detail.line_items.field.description.placeholder')}
                  size="sm"
                  truncate
                />
              </Styled.ReviewEntryLineItemCellField>
            )}
          />
        </Styled.ReviewEntryLineItemFormItemWrapper>
      </Styled.ReviewEntryLineItemCell>
      <Styled.ReviewEntryLineItemCell role="cell">
        <Styled.ReviewEntryLineItemFormItemWrapper>
          <FormItem
            name={`entryLines.${lineIndex}.vatRateId`}
            render={({ field: { error, id, value } }) => (
              <>
                <Styled.ReviewEntryLineItemFieldLabel htmlFor={id}>
                  {t('review_entries.detail.line_items.header.vat')}
                </Styled.ReviewEntryLineItemFieldLabel>
                <Styled.ReviewEntryLineItemCellField>
                  <ReviewEntryVatRateSelect
                    disabled={isDisabled}
                    dropdownSize="xxl"
                    error={error}
                    id={id}
                    onSelect={(selectedId, taxRate) => onVatRateChange(lineIndex, selectedId, taxRate)}
                    placeholder={t('review_entries.detail.line_items.field.vat_rate.placeholder')}
                    selectedId={value ?? undefined}
                    size="sm"
                  />
                </Styled.ReviewEntryLineItemCellField>
              </>
            )}
          />
        </Styled.ReviewEntryLineItemFormItemWrapper>
      </Styled.ReviewEntryLineItemCell>
      <Styled.ReviewEntryLineItemCell alignment="right" role="cell">
        <Styled.ReviewEntryLineItemFormItemWrapper>
          <FormItem
            name={`entryLines.${lineIndex}.amountExclVat`}
            render={({ field: { error, id, name, value } }) => (
              <Styled.ReviewEntryLineItemCellField>
                <InputNumberLocalized
                  alignment="right"
                  aria-label={t('review_entries.detail.line_items.totals.excl_vat.label')}
                  disabled={isDisabled}
                  error={error}
                  id={id}
                  name={name}
                  onChange={(newValue) => onAmountExclVatChange(lineIndex, newValue)}
                  size="sm"
                  value={value}
                />
              </Styled.ReviewEntryLineItemCellField>
            )}
          />
        </Styled.ReviewEntryLineItemFormItemWrapper>
      </Styled.ReviewEntryLineItemCell>
      <Styled.ReviewEntryLineItemCell alignment="right" role="cell">
        <ReviewEntryLineItemReadOnlyAmountField
          isDisabled
          label={t('review_entries.detail.line_items.field.vat_amount.label')}
          name={`entryLines.${lineIndex}.vatAmount`}
        />
      </Styled.ReviewEntryLineItemCell>
      <Styled.ReviewEntryLineItemCell alignment="right" role="cell">
        <Styled.ReviewEntryLineItemFormItemWrapper>
          <FormItem
            name={`entryLines.${lineIndex}.amountInclVat`}
            render={({ field: { error, id, name, value } }) => (
              <Styled.ReviewEntryLineItemCellField>
                <InputNumberLocalized
                  alignment="right"
                  aria-label={t('review_entries.detail.line_items.field.incl_vat.label')}
                  disabled={isDisabled}
                  error={error}
                  id={id}
                  name={name}
                  onChange={(newValue) => onAmountInclVatChange(lineIndex, newValue)}
                  size="sm"
                  value={value}
                />
              </Styled.ReviewEntryLineItemCellField>
            )}
          />
        </Styled.ReviewEntryLineItemFormItemWrapper>
      </Styled.ReviewEntryLineItemCell>
      <Styled.ReviewEntryLineItemActionWrapper>
        {showDeleteAction && (
          <IconButton
            aria-label={t('review_entries.detail.line_items.delete_line.aria_label')}
            icon="trash"
            onClick={handleDeleteClick}
            size="sm"
            variant="filled"
          />
        )}
      </Styled.ReviewEntryLineItemActionWrapper>
    </Styled.ReviewEntryLineItemRow>
  )
}
